import type { Overlay } from '../overlay/overlay.model';
import type { Story } from '../story/story.model';
import type { StoryFormat } from './story-format.model';

interface VideoWorkspaceSelection {
	format?: StoryFormat;
	story?: Story;
	overlays: Overlay[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isId(value: unknown): value is string | number {
	return typeof value === 'string' || typeof value === 'number';
}

function isStory(value: unknown): value is Story {
	return (
		isRecord(value) &&
		isId(value['id']) &&
		Array.isArray(value['storySegments']) &&
		Array.isArray(value['surveys']) &&
		Array.isArray(value['overlays']) &&
		isRecord(value['audioSettings'])
	);
}

function isFormat(value: unknown): value is StoryFormat {
	return (
		isRecord(value) &&
		isId(value['id']) &&
		typeof value['canvasWidth'] === 'number' &&
		typeof value['canvasHeight'] === 'number' &&
		Array.isArray(value['stories']) &&
		value['stories'].every(isStory) &&
		Array.isArray(value['overlays'])
	);
}

function isOverlay(value: unknown): value is Overlay {
	return isRecord(value) && isId(value['id']) && typeof value['title'] === 'string' && Array.isArray(value['fields']);
}

function savedSelection(snapshot: unknown, name: string): { active?: string | number; entity?: unknown } {
	if (!isRecord(snapshot) || !isRecord(snapshot[name])) {
		return {};
	}
	const store = snapshot[name];
	const active = store['active'];
	if (!isId(active)) {
		return {};
	}
	return { active, entity: isRecord(store['entities']) ? store['entities'][active] : undefined };
}

function sameId(left: unknown, right: unknown): boolean {
	return isId(left) && isId(right) && String(left) === String(right);
}

// Compare identifiers only: unchanged IDs should still receive current CMS copy/configuration.
function identityTree(value: unknown): string {
	const identities: string[] = [];
	const visit = (node: unknown, path: string) => {
		if (Array.isArray(node)) {
			node.forEach(child => visit(child, path));
		} else if (isRecord(node)) {
			for (const [key, child] of Object.entries(node)) {
				if (key === 'id' && isId(child)) {
					identities.push(`${path}:${child}`);
				} else {
					visit(child, `${path}.${key}`);
				}
			}
		}
	};
	visit(value, '');
	return JSON.stringify(identities.sort());
}

function savedOverlays(...sources: unknown[]): Overlay[] {
	const overlays = new Map<string, Overlay>();
	const visit = (node: unknown) => {
		if (Array.isArray(node)) {
			node.forEach(visit);
		} else if (isRecord(node)) {
			for (const [key, child] of Object.entries(node)) {
				if (key === 'overlays' && Array.isArray(child)) {
					for (const overlay of child) {
						if (isOverlay(overlay)) {
							overlays.set(String(overlay['id']), overlay);
						}
					}
				}
				visit(child);
			}
		}
	};
	sources.forEach(visit);
	return [...overlays.values()];
}

export function selectVideoWorkspace(
	snapshot: unknown,
	formats: readonly StoryFormat[],
	overlays: readonly Overlay[]
): VideoWorkspaceSelection {
	const formatSelection = savedSelection(snapshot, 'story-format');
	const storySelection = savedSelection(snapshot, 'story');
	const currentFormat =
		formatSelection.active === undefined ? formats[0] : formats.find(format => sameId(format.id, formatSelection.active));
	const savedFormat = isFormat(formatSelection.entity) ? formatSelection.entity : undefined;
	const currentStory = currentFormat?.stories.find(story => sameId(story.id, storySelection.active));
	const savedStory = isStory(storySelection.entity)
		? storySelection.entity
		: savedFormat?.stories.find(story => sameId(story.id, storySelection.active));
	const historicalOverlays = savedOverlays(savedFormat, savedStory, isRecord(snapshot) ? snapshot['UI/story-segment'] : undefined);
	const identitiesChanged =
		(savedFormat && (!currentFormat || identityTree(savedFormat) !== identityTree(currentFormat))) ||
		(savedStory && (!currentStory || identityTree(savedStory) !== identityTree(currentStory))) ||
		historicalOverlays.some(saved => {
			const current = overlays.find(overlay => sameId(overlay.id, saved.id));
			return !current || identityTree(saved) !== identityTree(current);
		});
	return {
		format: identitiesChanged ? savedFormat || currentFormat : currentFormat || savedFormat,
		story: identitiesChanged ? savedStory || currentStory : currentStory || savedStory,
		overlays: identitiesChanged ? historicalOverlays : []
	};
}
